import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getChannels } from "../api/channels";
import { deleteMessage as deleteMessageRequest, getMessages, postMessage } from "../api/messages";
import { createChatSocket } from "../sockets/socket";
import "./Home.css";

function unwrapChannelList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.channels)) return data.channels;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.channels)) return data.data.channels;
  return [];
}

function normalizeChannel(channel) {
  return {
    ...channel,
    id: channel.id ?? channel.channel_id ?? channel.channelId,
    name: channel.name ?? channel.channel_name ?? "",
    description: channel.description || "",
    archived: channel.is_archived === true || channel.isArchived === true || channel.archived === true,
  };
}

function unwrapMessageList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.messages)) return data.messages;
  if (Array.isArray(data?.replies)) return data.replies;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.messages)) return data.data.messages;
  if (Array.isArray(data?.data?.replies)) return data.data.replies;
  return [];
}

function formatMessageTime(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function sortNewestFirst(messages) {
  return [...messages].sort((first, second) => {
    const firstTime = first.createdAt ? new Date(first.createdAt).getTime() : Number.NEGATIVE_INFINITY;
    const secondTime = second.createdAt ? new Date(second.createdAt).getTime() : Number.NEGATIVE_INFINITY;
    return (Number.isNaN(secondTime) ? Number.NEGATIVE_INFINITY : secondTime)
      - (Number.isNaN(firstTime) ? Number.NEGATIVE_INFINITY : firstTime);
  });
}

function getReplyCount(message) {
  return Math.max(Number(message.reply_count ?? message.replyCount ?? 0), message.replies?.length || 0);
}

function normalizeMessage(message, channels) {
  const authorValue = message.author ?? message.user ?? message.sender;
  const authorFirstName = message.first_name ?? message.firstName ?? authorValue?.first_name ?? authorValue?.firstName;
  const authorLastName = message.last_name ?? message.lastName ?? authorValue?.last_name ?? authorValue?.lastName;
  const fullName = [authorFirstName, authorLastName].filter(Boolean).join(" ");
  const author = fullName || (typeof authorValue === "string"
    ? authorValue
    : authorValue?.name || authorValue?.username || message.username || message.user_name || "Member");
  const text = message.text ?? message.content ?? message.body ?? message.message ?? "";
  const timestamp = message.time ?? message.created_at ?? message.createdAt ?? message.timestamp;
  const initials = author.split(/[.\s_-]+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "ME";
  const colors = ["sage", "coral", "gold", "blue"];
  const color = colors[author.split("").reduce((sum, character) => sum + character.charCodeAt(0), 0) % colors.length];
  const channelValue = message.channel_name ?? message.channelName ?? message.channel;
  const channelName = typeof channelValue === "string" ? channelValue.replace(/^#/, "") : channelValue?.name;
  const channelId = message.channel_id ?? message.channelId ?? (typeof channelValue === "object" ? channelValue?.id : undefined);
  const channel = channels.find((item) =>
    (channelName && item.name.toLowerCase() === channelName.toLowerCase()) ||
    (channelId != null && String(item.id) === String(channelId)),
  );

  return {
    id: message.id ?? message.message_id ?? message.messageId,
    author,
    initials,
    time: formatMessageTime(timestamp),
    createdAt: timestamp,
    color: message.color || color,
    text: String(text),
    is_deleted: message.is_deleted === true,
     replyCount: Number(message.reply_count ?? message.replyCount ?? 0),
     replies: sortNewestFirst(unwrapMessageList(message.replies).map((reply) => normalizeMessage(reply, channels))),
    channel: channel?.name || channels[0]?.name || "",
  };
}

function normalizeMessageList(data, channels) {
  return sortNewestFirst(unwrapMessageList(data).map((message) => normalizeMessage(message, channels)));
}

function upsertMessage(messages, incoming) {
  if (incoming.id == null) return messages;
  const index = messages.findIndex((message) => String(message.id) === String(incoming.id));
  if (index === -1) return [...messages, incoming];

  const current = messages[index];
  const merged = {
    ...current,
    ...incoming,
    replies: mergeMessages(current.replies || [], incoming.replies || []),
  };
  return messages.map((message, messageIndex) => messageIndex === index ? merged : message);
}

function mergeMessages(current, incoming) {
  return sortNewestFirst(incoming.reduce(upsertMessage, current));
}

function applyChannelEvent(messages, type, payload, channels) {
  const rawMessage = type === "messageDeleted" ? payload?.message : payload;
  if (!rawMessage || rawMessage.id == null) return messages;
  const message = normalizeMessage(rawMessage, channels);
  const parentId = rawMessage.parent_id ?? rawMessage.parentId;

  if (type === "newThreadReply" || parentId != null) {
    return messages.map((parent) => String(parent.id) === String(parentId)
      ? { ...parent, replies: sortNewestFirst(upsertMessage(parent.replies || [], message)) }
      : parent,
    );
  }

  return sortNewestFirst(upsertMessage(messages, message));
}

export default function Home({ token, isAuthenticated = false, displayName = "", isAdmin = false, canDeleteMessages = false, onLogout }) {
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState("");
  const [messages, setMessages] = useState({});
  const [channelsLoading, setChannelsLoading] = useState(Boolean(token));
  const [channelsError, setChannelsError] = useState("");
  const [messagesLoading, setMessagesLoading] = useState(Boolean(token));
  const [messagesError, setMessagesError] = useState("");
  const messagesRef = useRef(messages);
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [channelSearch, setChannelSearch] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const socketRef = useRef(null);
  useEffect(() => {
    if (!token) {
      setChannels([]);
      setActiveChannel("");
      setMessages({});
      setChannelsLoading(false);
      setMessagesLoading(false);
      setChannelsError("Sign in to view workspace channels.");
      setMessagesError("");
      return undefined;
    }

    let isActive = true;
    async function loadWorkspace() {
      setChannelsLoading(true);
      setMessagesLoading(true);
      setChannelsError("");
      setMessagesError("");

      try {
          const channelResponse = await getChannels(token);
        const availableChannels = unwrapChannelList(channelResponse.data)
          .map(normalizeChannel)
          .filter((channel) => channel.name && !channel.archived);
        if (!isActive) return;
        setChannels(availableChannels);
        const selectedChannel = availableChannels[0];
        setActiveChannel(selectedChannel?.name || "");
        setChannelsLoading(false);

        if (!selectedChannel && isActive) setMessages({});
      } catch (requestError) {
        if (isActive) {
          setChannelsError(requestError.response?.data?.message || requestError.response?.data?.error || "Unable to load workspace channels.");
          setChannels([]);
          setActiveChannel("");
          setMessages({});
        }
      } finally {
        if (isActive) {
          setChannelsLoading(false);
          setMessagesLoading(false);
        }
      }
    }

    loadWorkspace();

    return () => {
      isActive = false;
    };
  }, [token]);

  const visibleChannels = channels.filter((channel) =>
    channel.name.toLowerCase().includes(channelSearch.trim().toLowerCase()),
  );
  const activeChannelInfo = channels.find((channel) => channel.name === activeChannel);
  const nameForAvatar = displayName.split("@")[0];
  const initials = nameForAvatar
    .split(/[.\s_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "ME";
  const activeMessages = messages[activeChannel] || [];
  messagesRef.current = messages;

  useEffect(() => {
    if (!token || !activeChannelInfo?.id) {
      setMessagesLoading(false);
      return undefined;
    }

    const channelId = String(activeChannelInfo.id);
    const channelName = activeChannelInfo.name;
    let isActive = true;
    let isBuffering = true;
    let isJoined = false;
    let bufferedEvents = [];
    setMessagesLoading(true);
    setMessagesError("");
    const socket = createChatSocket(token);
    socketRef.current = socket;

    function receiveMessageEvent(type, payload) {
      const message = type === "messageDeleted" ? payload?.message : payload;
      if (!message || String(message.channel_id) !== channelId) return;
      if (isBuffering) {
        bufferedEvents.push({ type, payload });
        return;
      }
      setMessages((currentMessages) => ({
        ...currentMessages,
        [channelName]: applyChannelEvent(currentMessages[channelName] || [], type, payload, channels),
      }));
    }

    async function fetchAndMergeHistory() {
      try {
        const response = await getMessages(token, activeChannelInfo.id);
        if (!isActive) return;
        let merged = mergeMessages([], normalizeMessageList(response.data, channels));
        bufferedEvents.forEach(({ type, payload }) => {
          merged = applyChannelEvent(merged, type, payload, channels);
        });
        bufferedEvents = [];
        isBuffering = false;
        setMessages((currentMessages) => ({ ...currentMessages, [channelName]: merged }));
        setMessagesError("");
      } catch (requestError) {
        if (!isActive) return;
        let merged = messagesRef.current[channelName] || [];
        bufferedEvents.forEach(({ type, payload }) => {
          merged = applyChannelEvent(merged, type, payload, channels);
        });
        bufferedEvents = [];
        isBuffering = false;
        setMessages((currentMessages) => ({ ...currentMessages, [channelName]: merged }));
        setMessagesError(requestError.response?.data?.message || requestError.response?.data?.error || "Unable to load messages.");
      } finally {
        if (isActive) setMessagesLoading(false);
      }
    }

    function onConnect() {
      isJoined = false;
      isBuffering = true;
      bufferedEvents = [];
      setMessagesLoading(true);
      socket.timeout(5000).emit("joinChannel", { channel_id: channelId }, (timeoutError, result) => {
        if (!isActive) return;
        if (timeoutError || !result?.success) {
          isBuffering = false;
          setMessagesLoading(false);
          setMessagesError(timeoutError?.message || result?.error || "Unable to join this channel.");
          return;
        }
        isJoined = true;
        fetchAndMergeHistory();
      });
    }

    function onConnectError(error) {
      if (isActive) {
        setMessagesLoading(false);
        setMessagesError(error?.message || "Unable to connect to live updates.");
      }
    }

    function onChannelError(error) {
      if (isActive) {
        setMessagesLoading(false);
        setMessagesError(error?.error || error?.message || "Unable to join this channel.");
      }
    }

    function onAuthenticationError(error) {
      if (isActive) {
        setMessagesLoading(false);
        setMessagesError(error?.message || error?.error || "Your session is no longer valid.");
      }
      socket.disconnect();
    }

    socket.on("newMessage", (message) => receiveMessageEvent("newMessage", message));
    socket.on("newThreadReply", (message) => receiveMessageEvent("newThreadReply", message));
    socket.on("messageDeleted", (payload) => receiveMessageEvent("messageDeleted", payload));
    socket.on("channelError", onChannelError);
    socket.on("authenticationError", onAuthenticationError);
    socket.on("connect_error", onConnectError);
    socket.on("connect", onConnect);
    socket.connect();

    return () => {
      isActive = false;
      isBuffering = false;
      if (socket.connected && isJoined) socket.emit("leaveChannel", { channel_id: channelId }, () => {});
      socket.off("newMessage");
      socket.off("newThreadReply");
      socket.off("messageDeleted");
      socket.off("channelError", onChannelError);
      socket.off("authenticationError", onAuthenticationError);
      socket.off("connect_error", onConnectError);
      socket.off("connect", onConnect);
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [token, activeChannelInfo?.id, activeChannelInfo?.name, channels]);

  async function sendMessage(event) {
    event.preventDefault();
    const text = draft.trim();
    if (!text || !activeChannelInfo?.id || !token) return;

    setIsSending(true);
    setMessagesError("");
    try {
      await postMessage(token, { channel_id: activeChannelInfo.id, text });
      setDraft("");
      const response = await getMessages(token, activeChannelInfo.id);
      setMessages((currentMessages) => ({
        ...currentMessages,
        [activeChannel]: mergeMessages(currentMessages[activeChannel] || [], normalizeMessageList(response.data, channels)),
      }));
    } catch (requestError) {
      setMessagesError(requestError.response?.data?.message || requestError.response?.data?.error || "Unable to send or reload this message.");
    } finally {
      setIsSending(false);
    }
  }

  async function handleDeleteMessage(message) {
    if (!canDeleteMessages || message.id == null || !token) return;
    if (!window.confirm("Delete this message? Its text will be replaced and replies will be preserved.")) return;

    setDeletingMessageId(message.id);
    setMessagesError("");
    try {
      await deleteMessageRequest(token, message.id);
      setMessages((currentMessages) => ({
        ...currentMessages,
        [activeChannel]: (currentMessages[activeChannel] || []).map((parent) =>
          String(parent.id) === String(message.id)
            ? { ...parent, text: "[Message deleted]", is_deleted: true }
            : parent,
        ),
      }));
    } catch (requestError) {
      const messageText = requestError.response?.data?.message || requestError.response?.data?.error || "Unable to delete this message.";
      setMessagesError(messageText);
    } finally {
      setDeletingMessageId(null);
    }
  }

  function selectChannel(channel) {
    setActiveChannel(channel);
    setSidebarOpen(false);
  }

  return (
    <main className="yants-workspace">
      <header className="workspace-topbar">
        <button
          aria-label="Toggle channel navigation"
          aria-expanded={sidebarOpen}
          className="mobile-menu-button"
          onClick={() => setSidebarOpen((open) => !open)}
          type="button"
        >
          <span />
          <span />
          <span />
        </button>
        <Link className="workspace-brand" to="/" aria-label="Yants home">
          <span className="brand-mark">Y</span>
          <span>Yants</span>
        </Link>
        <label className="workspace-search">
          <span className="search-icon" aria-hidden="true" />
          <input
            aria-label="Search channels"
            onChange={(event) => setChannelSearch(event.target.value)}
            placeholder="Search channels"
            type="search"
            value={channelSearch}
          />
        </label>
        {isAuthenticated ? (
          <div className="topbar-profile">
            {isAdmin && <Link className="topbar-users-link" to="/admin/channels">Channels</Link>}
            {isAdmin && <Link className="topbar-users-link" to="/admin/users">Users</Link>}
            <Link className="profile-account" to="/profile" aria-label="Open your profile">
              <span className="profile-avatar">{initials}</span>
              <span className="profile-name">{displayName || "Member"}</span>
            </Link>
            <button className="topbar-logout" onClick={onLogout} type="button">Log out</button>
          </div>
        ) : (
          <div className="topbar-auth-actions">
            <Link className="topbar-login" to="/login">Log in</Link>
            <Link className="topbar-register" to="/register">Register</Link>
          </div>
        )}
      </header>

      <div className="workspace-body">
        <button
          aria-label="Close channel navigation"
          className={`sidebar-backdrop${sidebarOpen ? " is-visible" : ""}`}
          onClick={() => setSidebarOpen(false)}
          type="button"
        />
        <aside className={`channel-sidebar${sidebarOpen ? " is-open" : ""}`}>
          <div className="workspace-card">
            <span className="workspace-avatar">Y</span>
            <span className="workspace-card-copy">
              <strong>Yants Community</strong>
              <span>Community workspace</span>
            </span>
            <span className="workspace-chevron" aria-hidden="true">⌄</span>
          </div>

          <div className="sidebar-section-heading">
            <span>Channels</span>
            <span className="channel-count">{channels.length}</span>
          </div>
          <nav aria-label="Channels" className="channel-list">
            {visibleChannels.map((channel) => (
              <button
                aria-current={activeChannel === channel.name ? "page" : undefined}
                className={`channel-link${activeChannel === channel.name ? " is-active" : ""}`}
                key={channel.id ?? channel.name}
                onClick={() => selectChannel(channel.name)}
                type="button"
              >
                <span className="channel-hash" aria-hidden="true">#</span>
                <span className="channel-name">{channel.name}</span>
              </button>
            ))}
            {channelsLoading && <p className="empty-channel-search">Loading channels...</p>}
            {!channelsLoading && channelsError && (
              <p className="empty-channel-search" role="status">{channelsError}</p>
            )}
            {!channelsLoading && !channelsError && visibleChannels.length === 0 && (
              <p className="empty-channel-search">{channels.length ? "No matching channels" : "No channels available"}</p>
            )}
          </nav>

          <div className="sidebar-footer">
            <span className="footer-status-dot" />
            <span>Workspace is active</span>
          </div>
        </aside>

        <section aria-label={activeChannel ? `${activeChannel} channel` : "Channel conversation"} className="channel-view">
          <header className="channel-header">
            <div className="channel-title-group">
              <span className="channel-title-hash" aria-hidden="true">#</span>
              <div>
                <h1>{activeChannel || "Channels"}</h1>
                <p>{activeChannelInfo?.description || (activeChannel ? "Channel conversation" : "Select a channel to view messages.")}</p>
              </div>
            </div>
            <div className="channel-header-meta">
              {activeChannelInfo && <span className="member-count">{activeChannelInfo.is_public === false ? "Private" : "Public"} channel</span>}
            </div>
          </header>

          <div className="conversation-toolbar">
            <span className="toolbar-active">Messages</span>
          </div>

          <div className="conversation-stage">
            <div className="conversation-scroll">
              <div className="conversation-inner">
                {activeChannel && <div className="date-divider"><span>Today</span></div>}
                {messagesLoading && <p className="messages-state">Loading messages...</p>}
                {messagesError && <p className="messages-state" role="status">{messagesError}</p>}
                {!activeChannel && !channelsLoading && !channelsError && (
                  <p className="messages-state">Select an active channel to view its messages.</p>
                )}
                <div aria-live="polite" className="message-list">
                  {activeMessages.map((message) => {
                    const replies = getReplyCount(message);
                    const postContent = (
                      <>
                        <p className="message-post">{message.text}</p>
                        <span className="post-reply-count">
                          {replies ? `${replies} ${replies === 1 ? "reply" : "replies"}` : "Open message"}
                        </span>
                      </>
                    );
                    return (
                      <article className="message-row" key={message.id ?? `${message.time}-${message.text}`}>
                        {activeChannelInfo?.id != null && message.id != null ? (
                          <Link
                            aria-label={`Open message with ${replies} ${replies === 1 ? "reply" : "replies"}`}
                            className="message-post-link"
                            to={`/messages/${activeChannelInfo.id}/${message.id}`}
                          >
                            {postContent}
                          </Link>
                        ) : (
                          <div className="message-post-link">{postContent}</div>
                        )}
                        {canDeleteMessages && !message.is_deleted && message.id != null && (
                          <button
                            aria-label="Delete message"
                            className="message-delete-action"
                            disabled={String(deletingMessageId) === String(message.id)}
                            onClick={() => handleDeleteMessage(message)}
                            type="button"
                          >
                            {String(deletingMessageId) === String(message.id) ? "Deleting..." : "Delete"}
                          </button>
                        )}
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {isAuthenticated && activeChannel ? (
            <form className="message-composer" onSubmit={sendMessage}>
              <label className="sr-only" htmlFor="message-draft">Message {activeChannel}</label>
              <input
                autoComplete="off"
                id="message-draft"
                onChange={(event) => setDraft(event.target.value)}
                placeholder={`Message #${activeChannel}`}
                value={draft}
              />
              <div className="composer-footer">
                <span className="composer-hint">Enter to send</span>
                  <button className="send-button" disabled={!draft.trim() || isSending} type="submit">
                  <span>{isSending ? "Sending..." : "Send"}</span>
                  <span aria-hidden="true">↗</span>
                </button>
              </div>
            </form>
          ) : (
            <div className="guest-composer">
              <div>
                <strong>{isAuthenticated ? "No channel selected" : "Join the conversation"}</strong>
                <span>{isAuthenticated ? "There are no active channels available." : "Log in or create an account to view and send messages."}</span>
              </div>
              {!isAuthenticated && <Link to="/login">Log in</Link>}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
